import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatEUR, formatDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  TrendingUp,
  Wallet,
  AlertCircle,
  Calendar as CalIcon,
  ArrowRight,
  Plus,
  FileText,
  Users,
  Landmark,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/dashboard")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data) {
    return <div className="text-slate-500">Cargando resumen...</div>;
  }

  const stats = [
    {
      label: "Ingresos del mes",
      value: formatEUR(data.ingresos_mes),
      icon: TrendingUp,
      accent: "text-emerald-600 bg-emerald-50",
      testid: "stat-ingresos-mes",
    },
    {
      label: "Facturado este mes",
      value: formatEUR(data.facturado_mes),
      icon: FileText,
      accent: "text-blue-600 bg-blue-50",
      testid: "stat-facturado-mes",
    },
    {
      label: "Pendiente de cobro",
      value: formatEUR(data.pendientes_cobro),
      icon: Wallet,
      accent: "text-amber-600 bg-amber-50",
      testid: "stat-pendiente",
    },
    {
      label: "Vencidas",
      value: formatEUR(data.vencidas_importe),
      sub: `${data.vencidas_count} factura${data.vencidas_count === 1 ? "" : "s"}`,
      icon: AlertCircle,
      accent: "text-rose-600 bg-rose-50",
      testid: "stat-vencidas",
    },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight">
            Panel general
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Aquí tienes el resumen de tu actividad y próximos vencimientos.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/app/facturas/nueva">
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="quick-new-invoice"
            >
              <Plus className="w-4 h-4 mr-1" /> Nueva factura
            </Button>
          </Link>
          <Link to="/app/presupuestos/nuevo">
            <Button variant="outline" data-testid="quick-new-quote">
              <Plus className="w-4 h-4 mr-1" /> Nuevo presupuesto
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <Card key={s.label} className="p-5 stat-card" data-testid={s.testid}>
            <div className={`w-10 h-10 rounded-lg grid place-items-center ${s.accent}`}>
              <s.icon className="w-5 h-5" strokeWidth={2} />
            </div>
            <div className="mt-4 text-sm text-slate-500">{s.label}</div>
            <div className="mt-1 font-display text-2xl font-bold text-slate-900 tracking-tight">
              {s.value}
            </div>
            {s.sub && (
              <div className="text-xs text-slate-500 mt-1">{s.sub}</div>
            )}
          </Card>
        ))}
      </div>

      {/* Bank balance banner */}
      {data.bank_connections_count > 0 ? (
        <Link to="/app/banco" className="block">
          <Card className="p-5 bg-gradient-to-br from-slate-900 to-slate-800 text-white border-0 hover:shadow-xl transition" data-testid="bank-balance-card">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-blue-600 grid place-items-center">
                  <Landmark className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-400">
                    Saldo bancario · {data.bank_connections_count} cuenta{data.bank_connections_count === 1 ? "" : "s"}
                  </div>
                  <div className="font-display text-3xl font-bold mt-1" data-testid="dashboard-bank-balance">
                    {formatEUR(data.bank_balance)}
                  </div>
                </div>
              </div>
              <div className="text-sm text-slate-300 hidden md:flex items-center gap-1">
                Ver movimientos <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          </Card>
        </Link>
      ) : (
        <Link to="/app/banco" className="block">
          <Card className="p-5 border-dashed border-blue-300 bg-blue-50/50 hover:bg-blue-50 transition" data-testid="bank-connect-cta">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-blue-600 grid place-items-center">
                  <Landmark className="w-6 h-6 text-white" />
                </div>
                <div>
                  <div className="font-display font-bold text-slate-900">
                    Conecta tu banco
                  </div>
                  <div className="text-sm text-slate-600 mt-0.5">
                    Consulta saldo, movimientos y concilia facturas automáticamente
                  </div>
                </div>
              </div>
              <div className="text-sm text-blue-600 hidden md:flex items-center gap-1 font-medium">
                Conectar <ArrowRight className="w-4 h-4" />
              </div>
            </div>
          </Card>
        </Link>
      )}

      {/* Chart + Fiscal */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">
                Últimos 6 meses
              </div>
              <div className="font-display text-lg font-bold text-slate-900">
                Facturación
              </div>
            </div>
            <Badge variant="secondary">Ingresos anuales: {formatEUR(data.ingresos_anio)}</Badge>
          </div>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.chart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="month" stroke="#64748B" fontSize={12} />
                <YAxis stroke="#64748B" fontSize={12} tickFormatter={(v) => `${v}€`} />
                <Tooltip
                  formatter={(v) => formatEUR(v)}
                  contentStyle={{
                    background: "#0F172A",
                    border: "none",
                    borderRadius: 8,
                    color: "#F8FAFC",
                  }}
                />
                <Bar dataKey="value" fill="#2563EB" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">
                Calendario fiscal
              </div>
              <div className="font-display text-lg font-bold text-slate-900">
                Próximos vencimientos
              </div>
            </div>
            <CalIcon className="w-5 h-5 text-slate-400" />
          </div>
          <div className="space-y-3">
            {data.upcoming_fiscal.length === 0 ? (
              <div className="text-sm text-slate-500 py-6 text-center">
                Sin vencimientos próximos
              </div>
            ) : (
              data.upcoming_fiscal.map((e) => (
                <div
                  key={e.id}
                  className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition"
                  data-testid={`upcoming-${e.id}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-amber-50 grid place-items-center text-amber-700 font-semibold text-xs">
                    {e.tax_model}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-900 truncate">
                      {e.title}
                    </div>
                    <div className="text-xs text-slate-500">
                      {formatDate(e.date)}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          <Link to="/app/calendario">
            <Button
              variant="ghost"
              size="sm"
              className="w-full mt-2 text-blue-600 hover:text-blue-700"
              data-testid="calendar-link"
            >
              Ver calendario completo <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </Link>
        </Card>
      </div>

      {/* Latest invoices */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500">
              Actividad reciente
            </div>
            <div className="font-display text-lg font-bold text-slate-900">
              Últimas facturas
            </div>
          </div>
          <Link to="/app/facturas">
            <Button variant="ghost" size="sm" data-testid="invoices-link">
              Ver todas <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </Link>
        </div>
        {data.latest_invoices.length === 0 ? (
          <div className="text-center py-10">
            <FileText className="w-10 h-10 text-slate-300 mx-auto" />
            <p className="mt-3 text-sm text-slate-500">
              Aún no has emitido facturas.
            </p>
            <Link to="/app/facturas/nueva">
              <Button
                className="mt-4 bg-blue-600 hover:bg-blue-700"
                data-testid="empty-new-invoice"
              >
                Crear primera factura
              </Button>
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-4">Número</th>
                  <th className="py-2 pr-4">Cliente</th>
                  <th className="py-2 pr-4">Fecha</th>
                  <th className="py-2 pr-4">Estado</th>
                  <th className="py-2 pr-4 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {data.latest_invoices.map((inv) => (
                  <tr
                    key={inv.id}
                    className="border-b border-slate-100 hover:bg-slate-50"
                  >
                    <td className="py-3 pr-4 font-medium text-slate-900">
                      {inv.number}
                    </td>
                    <td className="py-3 pr-4 text-slate-700 truncate max-w-[200px]">
                      {inv.client_snapshot?.name}
                    </td>
                    <td className="py-3 pr-4 text-slate-600">
                      {formatDate(inv.issue_date)}
                    </td>
                    <td className="py-3 pr-4">
                      <StatusBadge status={inv.status} />
                    </td>
                    <td className="py-3 pr-4 text-right font-medium text-slate-900">
                      {formatEUR(inv.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pagada: "bg-emerald-100 text-emerald-700 border-emerald-200",
    emitida: "bg-blue-100 text-blue-700 border-blue-200",
    vencida: "bg-rose-100 text-rose-700 border-rose-200",
    borrador: "bg-slate-100 text-slate-700 border-slate-200",
    anulada: "bg-slate-100 text-slate-500 border-slate-200 line-through",
  };
  return (
    <span
      className={`inline-block px-2 py-0.5 text-xs rounded border ${
        map[status] || map.borrador
      }`}
    >
      {status}
    </span>
  );
}
